import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  Suspense,
  lazy,
} from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  toast,
} from '@tyndo/ui-kit';
import { useApiClient } from '@tyndo/hooks';
import { useAuthRoles } from '../../auth/useSyncAuthRoles';
import {
  Pencil,
  Plus,
  RotateCcw,
  Save,
  X,
  Lock,
  Unlock,
  LayoutTemplate,
  ChevronDown,
} from 'lucide-react';
import type { WidgetSize } from '@tyndo/types';
import WidgetGallery from './WidgetGallery';
import ConfigureDrawer from './ConfigureDrawer';
import { useUnsavedChangeGuard } from './useUnsavedChangeGuard';
import { WidgetCanvas } from '../WidgetCanvas';
import { mapApiDefinitions } from '../../lib/widgetMappers';
import { useModuleAccess } from '../../layout/ModuleAccessContext';
import { filterByModuleAccess } from '../../layout/entitlementGate';
import type {
  EditableLayout,
  LayoutWidgetInstance,
  WidgetDefinition,
  DashboardTemplateRow,
} from './types';

interface LandingPageEditorProps {
  /** The route key the layout belongs to (e.g. `home`, `dashboard.operator`). */
  routeKey: string;
  /** Rendered fallback when there is no saved layout / no widgets to show. */
  children: React.ReactNode;
}

/** A widget-supplied config form: receives the current config `value` and an
 *  `onChange` to write it back. The shape every entry in CONFIG_FORM_LOADERS
 *  resolves to. */
type ConfigFormComponent = React.ComponentType<{
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}>;

/** The federation shapes a config-form module may expose the form under. */
interface ConfigFormModule {
  PlantLayoutConfigForm?: ConfigFormComponent;
  widgets?: { PlantLayoutConfigForm?: ConfigFormComponent };
  default?: { PlantLayoutConfigForm?: ConfigFormComponent };
}

/** Federated lookup for widget-supplied custom config forms.
 *  Each entry maps a `widgetId` → lazy import of the form module.
 *  Today: only the SCADA plant_layout widget exposes a form (P2A.12).
 *  Additional modules wire themselves here as their config forms ship. */
const CONFIG_FORM_LOADERS: Record<string, () => Promise<{ default: ConfigFormComponent }>> = {
  // Compiled-in first-party module via shell rspack alias (scada-ui is a lazy
  // chunk, not a federated remote). The bundle arrives opaque (`unknown`) and
  // is narrowed to the modelled config-form surface at this seam.
  'scada.widget.plant_layout': () => import('@tyndo/scada-ui/module').then((imported: unknown) => {
    const m = imported as ConfigFormModule;
    return {
    default:
      m?.PlantLayoutConfigForm ??
      m?.widgets?.PlantLayoutConfigForm ??
      m?.default?.PlantLayoutConfigForm ??
      // Last-ditch fallback: render an empty form rather than crash.
      (() => null),
    };
  }),
};

/**
 * Top-level edit shell for landing-page dashboards.
 *
 * Wraps a rendered dashboard with an edit toolbar. While edit mode is off the
 * wrapped `children` render normally. While edit mode is on the toolbar
 * exposes:
 *   - Reset, Save, Cancel
 *   - Choose template (persona / industry)
 *   - Add widget (opens WidgetGallery)
 *   - Admin lock toggle (admin users only)
 *
 * Each widget instance gets a drag-handle, resize selector, configure /
 * duplicate / remove icons, and is keyboard-reorderable via arrow keys when
 * the handle is focused (Space-to-grab, Arrow-to-move, Enter/Escape to drop).
 *
 * The editor calls the widget service backend exclusively — it does not
 * embed any layout state in the URL. If the backend isn't reachable (the
 * parallel agent's service hasn't landed yet) the toolbar still works in
 * a degraded mode: every API call surfaces a toast instead of crashing,
 * the layout still renders from the seed `children`, and editing is
 * disabled until the service responds.
 */
/**
 * Normalise a list response that may be a bare array or an envelope — the
 * widget endpoints wrap rows under `items` (definitions) or return a bare
 * array (templates), and every JSON response also carries `__source`/`__freshAt`
 * metadata. Returns [] for anything unrecognised so callers can safely `.find`.
 */
function unwrapArray<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload as T[];
  if (payload && typeof payload === 'object') {
    const obj = payload as Record<string, unknown>;
    const candidate = obj.items ?? obj.entries ?? obj.data ?? obj.results;
    if (Array.isArray(candidate)) return candidate as T[];
  }
  return [];
}

/**
 * Coerce a `GET /widgets/layouts/{routeKey}` response into a well-formed
 * {@link EditableLayout}, or `null` when the payload is absent / malformed.
 *
 * The backend contract is an object `{ routeKey, isAdminLocked, widgets: [...] }`,
 * but a degraded/seeding backend (or the lightweight test mock) can return a
 * bare `[]`, an error envelope, or an object missing `widgets`. Storing any of
 * those verbatim left `layout`/`draft` as a non-null value whose `.widgets` was
 * `undefined`, so the `draft?.widgets.find(...)` memo below dereferenced
 * `undefined.find` and white-screened the landing route. Normalising here keeps
 * `layout`/`draft` an invariant: either a real layout with a `widgets` array, or
 * `null` (which falls back to the seed `children`). Never a half-shaped object.
 */
function coerceLayout(payload: unknown, routeKey: string): EditableLayout | null {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return null;
  }
  const obj = payload as Record<string, unknown>;
  const widgets = unwrapArray<LayoutWidgetInstance>(obj.widgets);
  // A response with no recognisable widget list isn't a usable layout — treat
  // it as "no saved layout" so the seed page renders instead of empty chrome.
  if (widgets.length === 0 && !Array.isArray(obj.widgets)) {
    return null;
  }
  return {
    routeKey: typeof obj.routeKey === 'string' ? obj.routeKey : routeKey,
    isAdminLocked: obj.isAdminLocked === true,
    widgets,
  };
}

const LandingPageEditor: React.FC<LandingPageEditorProps> = ({ routeKey, children }) => {
  const api = useApiClient();

  // Admin capability gates the admin-only affordances (editing an admin-locked
  // layout, toggling the lock). Roles come from the decoded JWT via the shell's
  // single source of truth (useAuthRoles) — the same hook AppShell/TopBar use —
  // which normalises the native-auth `role` claim into a roles array.
  const userRoles = useAuthRoles();
  const isAdmin = useMemo(
    () =>
      userRoles.some((r) => {
        const role = r.toLowerCase();
        return role === 'admin' || role === 'administrator';
      }),
    [userRoles],
  );

  // Server-known layout + a local working copy. `layout` is what came
  // from `GET /layouts/{routeKey}`; `draft` is the user's edits during
  // an edit session; `dirty` is true while they diverge.
  const [layout, setLayout] = useState<EditableLayout | null>(null);
  const [draft, setDraft] = useState<EditableLayout | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [rawDefinitions, setRawDefinitions] = useState<WidgetDefinition[]>([]);
  const [templates, setTemplates] = useState<DashboardTemplateRow[]>([]);

  // Widget definitions carry their owning `moduleId`, plus a label, a
  // description and category metadata — so an ungated definitions list leaks
  // the existence and purpose of modules the operator may not open, both in
  // the gallery and in every rendered tile's title. Gate them on the same
  // permitted-module set as navigation. Definitions with no moduleId are
  // shell-owned (not module-scoped) and are kept.
  const moduleAccess = useModuleAccess();
  const definitions = useMemo(
    () => filterByModuleAccess(moduleAccess, rawDefinitions, (d) => d.moduleId, true),
    [moduleAccess, rawDefinitions],
  );
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [configuringId, setConfiguringId] = useState<string | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [confirmTemplateId, setConfirmTemplateId] = useState<string | null>(null);
  const [backendAvailable, setBackendAvailable] = useState<boolean>(true);
  const [busy, setBusy] = useState(false);

  // Keyboard reorder state: which instance is being "grabbed" with the
  // space bar from a focused drag handle. While set, Up/Down arrow keys
  // bubble through the row's `onKeyDown` and reorder the draft list.
  const [grabbedId, setGrabbedId] = useState<string | null>(null);

  const dirty = useMemo(() => {
    if (!layout || !draft) return false;
    return JSON.stringify(layout) !== JSON.stringify(draft);
  }, [layout, draft]);

  const guard = useUnsavedChangeGuard(dirty);

  // ────────────────────────────────────────────────────────────────────
  // Initial fetch — layout, definitions, templates.
  // ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [layoutRes, defRes, tplRes] = await Promise.all([
          api.get<unknown>(`/widgets/layouts/${encodeURIComponent(routeKey)}`).catch(() => null),
          api.get<unknown>(`/widgets/definitions`).catch(() => null),
          api.get<unknown>(`/widgets/templates`).catch(() => null),
        ]);
        if (!alive) return;
        // The backend reachability signal is the raw response (a `null` here
        // means the request itself failed). Shape-validation happens via
        // `coerceLayout` so a reachable-but-degraded backend that returns a
        // bare `[]`/envelope degrades to the seed page rather than crashing.
        if (layoutRes != null) {
          setBackendAvailable(true);
          const layoutModel = coerceLayout(layoutRes, routeKey);
          if (layoutModel) {
            setLayout(layoutModel);
            setDraft(layoutModel);
          }
        } else {
          setBackendAvailable(false);
        }
        // List endpoints may return a bare array or an envelope
        // (`{ items | entries | data: [...] }` plus `__source`/`__freshAt`
        // metadata), so normalise to the array before storing — otherwise
        // `definitions.find(...)` blows up on the envelope object.
        // Definitions go through the API→UI mapper so widget NAMES render
        // (label ← displayName, supportedSizes ← supportedSizesJson) instead
        // of bare identifiers.
        if (defRes) setRawDefinitions(mapApiDefinitions(defRes));
        if (tplRes) setTemplates(unwrapArray<DashboardTemplateRow>(tplRes));
      } catch {
        if (alive) setBackendAvailable(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [api, routeKey]);

  // Re-resolve the configure-drawer's instance lazily so deletes during
  // edit mode close the drawer cleanly.
  const configuringInstance = useMemo(
    // `draft?.widgets` is guaranteed an array by `coerceLayout`, but guard the
    // `.find` against a half-shaped draft so this memo can never white-screen
    // the landing route (defence in depth — the original crash site).
    () => draft?.widgets?.find((w) => w.instanceId === configuringId) ?? null,
    [draft, configuringId],
  );
  const configuringDefinition = useMemo(
    () => definitions.find((d) => d.widgetId === configuringInstance?.widgetId) ?? null,
    [definitions, configuringInstance],
  );

  // ── Toolbar actions ────────────────────────────────────────────────
  const enterEditMode = () => {
    if (layout?.isAdminLocked && !isAdmin) {
      toast.error('This layout is admin-locked. Ask an administrator to unlock it.');
      return;
    }
    setEditMode(true);
  };

  const cancelEdits = () => {
    if (dirty) {
      guard.requestNavigate(window.location.pathname);
      // The guard will set `pendingNavigation` to the current path which
      // pops the dialog. The dialog's "Discard" path simply clears draft.
      return;
    }
    setEditMode(false);
  };

  const saveLayout = useCallback(async () => {
    if (!draft) return;
    setBusy(true);
    try {
      await api.put(`/widgets/layouts/${encodeURIComponent(routeKey)}`, {
        widgets: draft.widgets.map((w) => ({
          instanceId: w.instanceId,
          widgetId: w.widgetId,
          size: w.size,
          order: w.order,
          configJson: w.configJson,
        })),
      });
      setLayout(draft);
      toast.success('Dashboard saved');
      setEditMode(false);
    } catch {
      toast.error('Failed to save dashboard');
    } finally {
      setBusy(false);
    }
  }, [api, draft, routeKey]);

  const resetLayout = useCallback(async () => {
    setBusy(true);
    try {
      const next = await api.post<EditableLayout>(
        `/widgets/layouts/${encodeURIComponent(routeKey)}/reset`,
        {},
      );
      setLayout(next);
      setDraft(next);
      setConfirmReset(false);
      toast.success('Dashboard reset to defaults');
    } catch {
      toast.error('Failed to reset dashboard');
      setConfirmReset(false);
    } finally {
      setBusy(false);
    }
  }, [api, routeKey]);

  const applyTemplate = useCallback(
    async (template: DashboardTemplateRow) => {
      setBusy(true);
      try {
        const widgets = template.widgets.map((widgetId, idx) => ({
          instanceId: `tpl-${template.templateId}-${idx}`,
          widgetId,
          size: (definitions.find((d) => d.widgetId === widgetId)?.defaultSize ?? 'medium') as WidgetSize,
          order: idx,
          configJson: null,
          lockedByAdmin: false,
        }));
        // Apply locally — the user still has to click Save to persist.
        setDraft((prev) =>
          prev
            ? { ...prev, widgets }
            : { routeKey, isAdminLocked: false, widgets },
        );
        toast.success(`Applied template "${template.label}". Save to persist.`);
      } finally {
        setConfirmTemplateId(null);
        setBusy(false);
      }
    },
    [definitions, routeKey],
  );

  const toggleAdminLock = useCallback(async () => {
    if (!layout) return;
    const next = !layout.isAdminLocked;
    try {
      await api.post(`/widgets/layouts/${encodeURIComponent(routeKey)}/lock`, { locked: next });
      setLayout({ ...layout, isAdminLocked: next });
      setDraft((prev) => (prev ? { ...prev, isAdminLocked: next } : prev));
      toast.success(next ? 'Layout locked' : 'Layout unlocked');
    } catch {
      toast.error('Failed to update lock state');
    }
  }, [api, layout, routeKey]);

  // ── Widget instance operations (local-only until save) ──────────────
  const addWidget = (def: WidgetDefinition) => {
    setDraft((prev) => {
      const base = prev ?? { routeKey, isAdminLocked: false, widgets: [] };
      const nextOrder = base.widgets.length;
      const instance: LayoutWidgetInstance = {
        instanceId: `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        widgetId: def.widgetId,
        size: def.defaultSize ?? def.supportedSizes?.[0] ?? 'medium',
        order: nextOrder,
        configJson: null,
        lockedByAdmin: false,
      };
      return { ...base, widgets: [...base.widgets, instance] };
    });
    setGalleryOpen(false);
  };

  const removeWidget = (instanceId: string) => {
    setDraft((prev) =>
      prev ? { ...prev, widgets: prev.widgets.filter((w) => w.instanceId !== instanceId) } : prev,
    );
  };

  const duplicateWidget = (instanceId: string) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const src = prev.widgets.find((w) => w.instanceId === instanceId);
      if (!src) return prev;
      const clone: LayoutWidgetInstance = {
        ...src,
        instanceId: `dup-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        order: prev.widgets.length,
      };
      return { ...prev, widgets: [...prev.widgets, clone] };
    });
  };

  const resizeWidget = (instanceId: string, size: WidgetSize) => {
    setDraft((prev) =>
      prev
        ? {
            ...prev,
            widgets: prev.widgets.map((w) => (w.instanceId === instanceId ? { ...w, size } : w)),
          }
        : prev,
    );
  };

  const configureWidget = (instanceId: string, configJson: string) => {
    setDraft((prev) =>
      prev
        ? {
            ...prev,
            widgets: prev.widgets.map((w) =>
              w.instanceId === instanceId ? { ...w, configJson } : w,
            ),
          }
        : prev,
    );
  };

  const reorderWidget = (instanceId: string, direction: -1 | 1) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const idx = prev.widgets.findIndex((w) => w.instanceId === instanceId);
      if (idx === -1) return prev;
      const target = idx + direction;
      if (target < 0 || target >= prev.widgets.length) return prev;
      const next = [...prev.widgets];
      const [moved] = next.splice(idx, 1);
      if (!moved) return prev;
      next.splice(target, 0, moved);
      return { ...prev, widgets: next.map((w, i) => ({ ...w, order: i })) };
    });
  };

  // HTML5 native drag-and-drop — keyboard-fallback handled via the
  // arrow-key reorder when `grabbedId` is set.
  const dragSrcRef = useRef<string | null>(null);
  const onDragStart = (instanceId: string) => {
    dragSrcRef.current = instanceId;
  };
  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };
  const onDrop = (targetInstanceId: string) => {
    const src = dragSrcRef.current;
    dragSrcRef.current = null;
    if (!src || src === targetInstanceId) return;
    setDraft((prev) => {
      if (!prev) return prev;
      const srcIdx = prev.widgets.findIndex((w) => w.instanceId === src);
      const tgtIdx = prev.widgets.findIndex((w) => w.instanceId === targetInstanceId);
      if (srcIdx === -1 || tgtIdx === -1) return prev;
      const next = [...prev.widgets];
      const [moved] = next.splice(srcIdx, 1);
      if (!moved) return prev;
      next.splice(tgtIdx, 0, moved);
      return { ...prev, widgets: next.map((w, i) => ({ ...w, order: i })) };
    });
  };

  // ── Keyboard handler for the drag handle ──────────────────────────
  const onHandleKeyDown = (e: React.KeyboardEvent, instanceId: string) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      setGrabbedId((cur) => (cur === instanceId ? null : instanceId));
      return;
    }
    if (e.key === 'Escape' && grabbedId === instanceId) {
      setGrabbedId(null);
      return;
    }
    if (grabbedId === instanceId) {
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        reorderWidget(instanceId, -1);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        reorderWidget(instanceId, 1);
      }
    }
  };

  // ────────────────────────────────────────────────────────────────────
  // Render
  // ────────────────────────────────────────────────────────────────────

  // A SAVED layout can still name a widget whose owning module the operator is
  // no longer entitled to (entitlement revoked, persona switched, layout
  // inherited from a template). Drop those instances before rendering: a tile
  // would otherwise surface the widget's title and fetch its module data.
  // Instances whose widgetId the server never defined are left alone — they
  // carry no module metadata to leak and already render an unknown-widget
  // placeholder.
  const allWidgets = draft?.widgets ?? layout?.widgets ?? [];
  const suppressedWidgetIds = new Set(
    rawDefinitions
      .filter((d) => !definitions.some((allowed) => allowed.widgetId === d.widgetId))
      .map((d) => d.widgetId),
  );
  const widgets = allWidgets.filter((w) => !suppressedWidgetIds.has(w.widgetId));
  const adminLocked = (draft ?? layout)?.isAdminLocked ?? false;
  const canEdit = !adminLocked || isAdmin;
  const personaTemplates = templates.filter((t) => t.scope?.includes('persona') || t.personas?.length);
  const industryTemplates = templates.filter(
    (t) => t.scope?.includes('industry') || t.industries?.length,
  );

  return (
    <div className="relative" data-testid="landing-page-editor">
      {/* Toolbar */}
      <div
        className="sticky top-0 z-20 bg-surface-raised/80 backdrop-blur-md border-b border-border px-4 py-2 flex items-center gap-2 flex-wrap"
        role="toolbar"
        aria-label="Dashboard editor"
      >
        {adminLocked && (
          <span
            className="inline-flex items-center gap-1 text-xs text-muted-foreground"
            data-testid="lock-indicator"
          >
            <Lock className="h-3 w-3" aria-hidden="true" />
            Layout locked
          </span>
        )}

        {!editMode ? (
          <Button
            size="sm"
            variant="default"
            onClick={enterEditMode}
            disabled={!canEdit || !backendAvailable}
            data-testid="editor-edit"
            aria-label="Edit dashboard"
          >
            <Pencil className="h-3 w-3 mr-1" aria-hidden="true" />
            Edit
          </Button>
        ) : (
          <>
            <Button
              size="sm"
              variant="ghost"
              onClick={cancelEdits}
              data-testid="editor-cancel"
              aria-label="Cancel edits"
            >
              <X className="h-3 w-3 mr-1" aria-hidden="true" />
              Cancel
            </Button>
            <Button
              size="sm"
              variant="default"
              onClick={saveLayout}
              disabled={!dirty || busy}
              data-testid="editor-save"
              aria-label="Save dashboard"
            >
              <Save className="h-3 w-3 mr-1" aria-hidden="true" />
              Save
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setConfirmReset(true)}
              data-testid="editor-reset"
              aria-label="Reset dashboard to defaults"
            >
              <RotateCcw className="h-3 w-3 mr-1" aria-hidden="true" />
              Reset
            </Button>

            {/* Choose template */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="editor-template"
                  aria-label="Choose template"
                >
                  <LayoutTemplate className="h-3 w-3 mr-1" aria-hidden="true" />
                  Choose template
                  <ChevronDown className="h-3 w-3 ml-1" aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="max-h-80 overflow-auto">
                {personaTemplates.length > 0 && (
                  <>
                    <DropdownMenuLabel>By persona</DropdownMenuLabel>
                    {personaTemplates.map((t) => (
                      <DropdownMenuItem
                        key={`persona-${t.templateId}`}
                        onClick={() => setConfirmTemplateId(t.templateId)}
                        data-testid={`editor-template-persona-${t.templateId}`}
                      >
                        {t.label}
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                  </>
                )}
                {industryTemplates.length > 0 && (
                  <>
                    <DropdownMenuLabel>By industry</DropdownMenuLabel>
                    {industryTemplates.map((t) => (
                      <DropdownMenuItem
                        key={`industry-${t.templateId}`}
                        onClick={() => setConfirmTemplateId(t.templateId)}
                        data-testid={`editor-template-industry-${t.templateId}`}
                      >
                        {t.label}
                      </DropdownMenuItem>
                    ))}
                  </>
                )}
                {personaTemplates.length === 0 && industryTemplates.length === 0 && (
                  <DropdownMenuItem disabled>No templates available</DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Add widget */}
            <Button
              size="sm"
              variant="outline"
              onClick={() => setGalleryOpen(true)}
              data-testid="editor-add-widget"
              aria-label="Add widget"
            >
              <Plus className="h-3 w-3 mr-1" aria-hidden="true" />
              Add widget
            </Button>

            {isAdmin && (
              <Button
                size="sm"
                variant="ghost"
                onClick={toggleAdminLock}
                data-testid="editor-toggle-lock"
                aria-label={adminLocked ? 'Unlock layout' : 'Lock layout (admin only)'}
              >
                {adminLocked ? (
                  <Unlock className="h-3 w-3 mr-1" aria-hidden="true" />
                ) : (
                  <Lock className="h-3 w-3 mr-1" aria-hidden="true" />
                )}
                {adminLocked ? 'Unlock' : 'Lock'}
              </Button>
            )}

            <span
              aria-live="polite"
              className="ml-auto text-xs text-muted-foreground"
              data-testid="editor-dirty-indicator"
            >
              {dirty ? 'Unsaved changes' : 'All changes saved'}
            </span>
          </>
        )}
      </div>

      {/* Body */}
      {editMode ? (
        <div className="p-4">
          {widgets.length === 0 ? (
            <div className="glass rounded-3xl border border-dashed border-border p-8 text-center">
              <p className="text-sm text-muted-foreground">
                This dashboard has no widgets yet. Click <strong>Add widget</strong> to start.
              </p>
            </div>
          ) : (
            <WidgetCanvas
              editing
              widgets={widgets}
              definitions={definitions}
              grabbedId={grabbedId}
              onRemove={removeWidget}
              onDuplicate={duplicateWidget}
              onResize={resizeWidget}
              onConfigure={setConfiguringId}
              onDragStart={onDragStart}
              onDragOver={onDragOver}
              onDrop={onDrop}
              onHandleKeyDown={onHandleKeyDown}
            />
          )}
        </div>
      ) : widgets.length > 0 ? (
        /* Out of edit mode: render the SAVED layout (the core fix). When the
           layout is empty, fall back to the seed `children` (PlantCanvas). */
        <div className="p-4">
          <WidgetCanvas widgets={widgets} definitions={definitions} editing={false} />
        </div>
      ) : (
        children
      )}

      {/* Gallery */}
      <WidgetGallery
        open={galleryOpen}
        onOpenChange={setGalleryOpen}
        definitions={definitions}
        onAdd={addWidget}
      />

      {/* Configure drawer */}
      <ConfigureDrawerWithLazyForm
        open={configuringId !== null}
        onOpenChange={(o) => !o && setConfiguringId(null)}
        instance={configuringInstance}
        definition={configuringDefinition}
        onSave={configureWidget}
      />

      {/* Reset confirm */}
      <Dialog open={confirmReset} onOpenChange={setConfirmReset}>
        <DialogContent data-testid="editor-reset-dialog">
          <DialogHeader>
            <DialogTitle>Reset dashboard?</DialogTitle>
            <DialogDescription>
              This replaces your current layout with the route's default template. Your widgets,
              sizes, and ordering will be lost.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmReset(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={resetLayout}
              data-testid="editor-reset-confirm"
              disabled={busy}
            >
              Reset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template confirm */}
      <Dialog
        open={confirmTemplateId !== null}
        onOpenChange={(o) => !o && setConfirmTemplateId(null)}
      >
        <DialogContent data-testid="editor-template-dialog">
          <DialogHeader>
            <DialogTitle>Apply this template?</DialogTitle>
            <DialogDescription>
              Your current widget list will be replaced by the template's widget set. You can
              still cancel before clicking Save.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmTemplateId(null)}>
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={() => {
                const tpl = templates.find((t) => t.templateId === confirmTemplateId);
                if (tpl) applyTemplate(tpl);
              }}
              data-testid="editor-template-confirm"
              disabled={busy}
            >
              Apply template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unsaved-change guard dialog */}
      <Dialog
        open={guard.pendingNavigation !== null}
        onOpenChange={(o) => !o && guard.cancelNavigation()}
      >
        <DialogContent data-testid="editor-unsaved-dialog">
          <DialogHeader>
            <DialogTitle>Discard unsaved changes?</DialogTitle>
            <DialogDescription>
              You have unsaved edits to this dashboard. If you leave, your changes will be lost.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={guard.cancelNavigation}>
              Continue editing
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                // Reset draft, then proceed.
                setDraft(layout);
                setEditMode(false);
                guard.confirmNavigation();
              }}
              data-testid="editor-unsaved-discard"
            >
              Discard changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/**
 * Wraps ConfigureDrawer with a lazy-loaded `customForm`. Resolving the
 * federated form only when needed prevents a top-level `scada_ui` import
 * from leaking into the shell's main chunk.
 */
const ConfigureDrawerWithLazyForm: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  instance: LayoutWidgetInstance | null;
  definition: WidgetDefinition | null;
  onSave: (instanceId: string, configJson: string) => void;
}> = ({ open, onOpenChange, instance, definition, onSave }) => {
  // Lazy resolution: keep the LazyForm component reference in state and
  // recompute when the widgetId changes. We don't cache across remounts
  // because federation handles its own caching.
  const widgetId = instance?.widgetId;
  const LazyForm = useMemo(() => {
    if (!widgetId) return null;
    const loader = CONFIG_FORM_LOADERS[widgetId];
    return loader ? lazy(loader) : null;
  }, [widgetId]);

  const SuspenseWrapped = useMemo(() => {
    if (!LazyForm) return undefined;
    const Wrapped: React.ComponentType<{
      value: Record<string, unknown>;
      onChange: (next: Record<string, unknown>) => void;
    }> = (props) => (
      <Suspense fallback={<p className="text-xs text-muted-foreground">Loading form…</p>}>
        <LazyForm {...props} />
      </Suspense>
    );
    return Wrapped;
  }, [LazyForm]);

  return (
    <ConfigureDrawer
      open={open}
      onOpenChange={onOpenChange}
      instance={instance}
      definition={definition}
      customForm={SuspenseWrapped}
      onSave={onSave}
    />
  );
};

export default LandingPageEditor;
